import React, { useEffect, useState } from 'react';
import RegistrationForm from './RegistrationForm';
import {
    Alert,
    Button,
    Divider,
    Link,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import i18n from '../../i18';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import helper from '../../utils/helper';
import LanguagePopover from '../../popovers/LanguagePopover';
import CodeAuthField from '../../components/code-auth-input/CodeAuthField';
import { useStateSelector } from '../../context/StateSelectors';
import { formatBackendError } from '../../utils/formatError';

export default function RegistrationPage() {
    const navigate = useNavigate();
    const server = useStateSelector((state) => state.server);

    const [step, setStep] = React.useState(1); // 1,2,3

    const [email, setEmail] = React.useState('');
    const [code, setCode] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState('');

    const handleEnterCode = (enteredCode) => {
        setCode(enteredCode);
        server.auth.register({ email, code: enteredCode }).then((result) => {
            if (result.error === 'OK') {
                setErrorMessage('');
                setStep((prevState) => prevState + 1);
            } else {
                setErrorMessage(formatBackendError(result));
            }
        });
    };
    const handleSendEmail = () => {
        if (!email) {
            setErrorMessage(i18n.t('auth.emailRequired'));
            return;
        }
        server.auth.register({ email }).then((result) => {
            if (result.error === 'OK') {
                setErrorMessage('');
                setStep((prevState) => prevState + 1);
            } else {
                setErrorMessage(formatBackendError(result));
            }
        });
    };
    const [timer, setTimer] = useState(300);
    const [canResend, setCanResend] = useState(false);
    const startResendTimer = () => {
        setTimer(300);
        setCanResend(false);
    };

    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prevState) => prevState - 1);
            }, 1000);
        } else {
            setCanResend(true);
            clearInterval(interval);
        }

        return () => clearInterval(interval);
    }, [timer]);

    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };
    const handleResendCode = () => {
        if (!canResend) return;
        server.auth.register({ email }).then((result) => {
            if (result.error === 'OK') {
                setErrorMessage('');
                startResendTimer();
            } else {
                setErrorMessage(formatBackendError(result));
            }
        });
    };
    return (
        <>
            <Helmet>
                <title>{helper.formPageTitle('auth.register')}</title>
            </Helmet>

            <Stack direction="row" justifyContent="space-between">
                <Typography variant="h4" gutterBottom>
                    {i18n.t('auth.registerTitle')}
                </Typography>

                <LanguagePopover forceSmall={true} />
            </Stack>
            <Divider sx={{ my: 3 }} />

            {step === 1 && (
                <>
                    <Stack spacing={3}>
                        <Typography variant="subtitle2">
                            {i18n.t('auth.registerDescription')}
                        </Typography>
                        <TextField
                            name="email"
                            label={i18n.t('columns.email')}
                            required
                            value={email}
                            error={!!errorMessage}
                            onChange={(event) => setEmail(event.target.value)}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleSendEmail(email)}
                        >
                            {i18n.t('auth.continue')}
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {errorMessage && (
                        <Alert severity="error">{errorMessage}</Alert>
                    )}

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ my: 2 }}
                    >
                        <Link
                            variant="subtitle2"
                            underline="hover"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate('/auth/login')}
                        >
                            {i18n.t('auth.loginLink')}
                        </Link>
                    </Stack>
                </>
            )}
            {step === 2 && (
                <>
                    <Stack spacing={3}>
                        <Typography variant="subtitle1">
                            {i18n.t('auth.enterCodeFromEmail')}
                        </Typography>
                        <CodeAuthField
                            callback={handleEnterCode}
                            codeLength={10}
                        />
                        <Typography variant="subtitle2">
                            {i18n.t('auth.didNotReceiveCode')}{' '}
                            <Link
                                underline={canResend ? 'hover' : 'none'}
                                sx={{
                                    cursor: canResend ? 'pointer' : 'default'
                                }}
                                onClick={handleResendCode}
                            >
                                {canResend
                                    ? i18n.t('auth.resendCode')
                                    : `${i18n.t(
                                        'auth.resendCodeIn'
                                    )} ${formatTime(timer)}`}
                            </Link>
                        </Typography>{errorMessage && (
                            <Alert severity="error">{errorMessage}</Alert>
                        )}
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ my: 2 }}
                    >
                        <Link
                            variant="subtitle2"
                            underline="hover"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                                setStep((prevState) => prevState - 1);
                                setErrorMessage('');
                            }
                            }
                        >
                            {i18n.t('auth.back')}
                        </Link>
                    </Stack>
                </>
            )}
            {step === 3 && (
                <RegistrationForm
                    handleChangeStep={() => {
                        setStep((prevState) => prevState - 2);
                        setErrorMessage('');
                    }
                    }
                    email={email}
                    code={code}
                />
            )}
        </>
    );
}
